/**
 * Display-boundary humanizer for sync-run error text.
 *
 * Failed appointment-pull runs store their error as `<taxonomy code>: <detail>`
 * (e.g. `login_failed: Invalid username/password`) — the code is the scrape
 * error taxonomy from docs/scraping-subsystem-architecture.md §3, kept verbatim
 * in Firestore so ops can grep/alert on it. Raw snake_case reads badly in the
 * UI, so this maps a leading known code to plain language at render time; the
 * stored data is untouched (same posture as scrubVendorText).
 *
 * Only the closed set of codes is mapped — anything else passes through
 * unchanged, so ordinary error prose is never mangled.
 *
 * WHY string parsing and not a typed error-code field: the scraper writes the
 * typed taxonomy into the same free-text `error` field that records of the
 * retired engine filled with prose (the bot's own wording, still stored), so
 * the UI pattern-matches the string and passes unknown text through. A
 * follow-up may store the code as its own field on the run doc and switch on
 * it per code — no prefix parsing.
 */

const CODE_LABELS: Record<string, string> = {
  login_failed: 'Login failed',
  portal_unavailable: 'Portal unavailable',
  selector_not_found: 'Cannot get information from the website. Support is working on it.',
  provider_not_found: "Provider not found on the portal account. Check the doctor on this appointment or the clinic's default provider, then re-run the check.",
  no_results: 'No appointments found',
  timeout: 'Data retrieval took too long',
  dispatch_failed: 'Could not start the sync',
  crash: 'Unexpected error. Support is working on it.',
  not_in_network: 'Practice not in network',
}

/**
 * Codes whose stored detail adds nothing for the user — pure diagnostics
 * (selector names, Playwright timeouts, which phase of the run the deadline
 * cut: before work began / mid-operation / after N of M patients) or a
 * restatement of the label (`no_results` detail just names the date the label
 * already implies). The label alone is the whole message; the full text stays
 * in Firestore and Cloud Logging for ops.
 */
// `crash` belongs here for the docs written before the worker started
// storing it code-only: their detail is a raw (sanitized) exception message.
// `not_in_network` stores VSP's own sentence, which restates the label and
// arrives with its error code masked to `[id]` by the sanitizer.
// `provider_not_found` stores the bot's own "Service Provider not Found" — the
// label already says it.
const SUPPRESS_DETAIL = new Set([
  'selector_not_found', 'provider_not_found', 'no_results', 'timeout', 'crash',
  'not_in_network',
])

/**
 * Bare-form overrides: a code stored with NO detail is the internal variant
 * (the dispatcher keeps lane internals off run docs — logs/Sentry only), so
 * the user can't act on it and the copy adds the support reassurance,
 * matching selector_not_found's wording. A detail-carrying dispatch_failed
 * is tenant-worded (e.g. the Revolution location misconfig) and keeps the
 * plain label + detail.
 */
const BARE_CODE_LABELS: Record<string, string> = {
  dispatch_failed: 'Could not start the sync. Support is working on it.',
}

/** Replace a leading `<code>:` taxonomy prefix (or a bare code) with its plain-language label. */
export function humanizeSyncError(text: string): string
export function humanizeSyncError(text: string | null | undefined): string | undefined
export function humanizeSyncError(text: string | null | undefined): string | undefined {
  if (text == null) return undefined
  const idx = text.indexOf(':')
  const head = (idx === -1 ? text : text.slice(0, idx)).trim().toLowerCase()
  const label = CODE_LABELS[head]
  if (!label) return text
  if (SUPPRESS_DETAIL.has(head)) return label
  const detail = idx === -1 ? '' : text.slice(idx + 1).trim()
  if (!detail) return BARE_CODE_LABELS[head] ?? label
  return `${label}: ${detail}`
}
